
import React from 'react';
import Logo from './Logo';
import Footer from './Footer';
import { Button } from './ui/button';
import { ChevronLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { TooltipProvider } from "@/components/ui/tooltip";
import ThemeToggle from './ThemeToggle';
import Navigation from './Navigation';
import MobileNavigation from './MobileNavigation';
import SearchTrigger from './SearchTrigger';

interface LayoutProps {
  children: React.ReactNode;
  showBackButton?: boolean;
}

// Use React.memo to optimize rendering
const Layout = React.memo(({ children, showBackButton = false }: LayoutProps) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  // Generate back link based on current path structure
  const getBackLink = () => {
    if (isHomePage) return '/';
    
    const pathSegments = location.pathname.split('/').filter(Boolean);
    
    // If we're on a calculator page (calculators/category/calculator), go back to category
    if (pathSegments.length === 3 && pathSegments[0] === 'calculators') {
      return `/calculators/${pathSegments[1]}`;
    }
    
    // Otherwise go to home
    return '/';
  };
  
  const backLink = getBackLink();

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        <header className="py-4 px-6 md:px-10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {showBackButton && !isHomePage && (
              <Button
                variant="ghost"
                size="sm"
                asChild
                className="gap-2 animate-fade-in"
              >
                <Link to={backLink}>
                  <ChevronLeft className="h-4 w-4" />
                  Back to Calculators
                </Link>
              </Button>
            )}
            <Logo />
            {/* Add a small margin here */}
            <div className="ml-4">
              <Navigation />
            </div>
          </div>
          
          <div className="flex items-center">
            <div className="flex items-center gap-4 ml-4">
              <SearchTrigger />
              <ThemeToggle />
              <MobileNavigation />
            </div>
          </div>
        </header>

        <main className="animate-fade-in" key={location.pathname}>
          {children}
        </main>

        <Footer />
      </div>
    </TooltipProvider>
  );
});

Layout.displayName = 'Layout';

export default Layout;
